import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MedicationDispense } from 'fhir/r4';
import { TableFilteringService, TableFilters, TableRowModel } from '../shared/table-filtering.service';
import { backendEndPointToken } from '../app.config';
import { LoadingUIEnabled } from '../loading.interceptor';

export type MedicationDispenseRow = MedicationDispense & TableRowModel;

@Injectable({
  providedIn: 'root'
})
export class MedicationDispenseSearchService {
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly tableFiltering = inject(TableFilteringService) as unknown as TableFilteringService<MedicationDispense, MedicationDispenseRow>;
  private readonly silentHttpContext = new HttpContext().set(LoadingUIEnabled, false);

  readonly dataSource = new MatTableDataSource<MedicationDispenseRow>();
  readonly loading$ = this.tableFiltering.loading$;
  readonly total$ = this.tableFiltering.total$;
  readonly error$ = this.tableFiltering.error$;

  init(patientId?: string | null): void {
    const defaultQuery = patientId 
      ? `/MedicationDispense?subject=Patient/${patientId}`
      : `/MedicationDispense`;

    this.tableFiltering.init({
      resourceType: 'MedicationDispense',
      defaultQuery,
      dataSource: this.dataSource,
      pageSize: 25,
      baseUrl: this.backendUrl,
      transformEntry: (resource: MedicationDispense) => this.transform(resource),
      httpOptions: { context: this.silentHttpContext },
      enableLocalFiltering: false
    });
  }

  destroy(): void {
    this.tableFiltering.destroy();
    this.dataSource.disconnect();
  }

  search(filters: TableFilters): void {
    this.tableFiltering.search(filters);
  }

  changePage(pageIndex: number): void {
    this.tableFiltering.changePage(pageIndex);
  }

  refresh(): void {
    this.tableFiltering.refreshCurrentPage();
  }

  private transform(resource: MedicationDispense): MedicationDispenseRow {
    return {
      ...resource,
      id: resource.id ?? '',
      resourceType: 'MedicationDispense',
      rawResource: resource
    };
  }
}
